import {type ReactNode, useEffect, useState} from 'react'
import {Text, View} from 'react-native'

import {GrapeIcon} from '#/view/icons/GrapeIcon'
import {Logotype} from '#/view/icons/Logotype'
import {atoms as a, flatten} from '#/alf'

const PURPLE = '#C22EFF'
const GLINT = '#F0C8FF'
const DENSITY = ['.', ':', 'o', 'O', '@'] as const
const CRYSTAL = ['.', '+', '*', '◆', '◇', '*', '+'] as const

/*
 * Lucide-shaped ASCII: stem up-right, berries cascading down-left.
 * Non-space cells animate through DENSITY; spaces stay empty.
 */
const ASCII_GRAPE_MASK = [
  '         #  ',
  '        #   ',
  '   ##  ##   ',
  '  ##  ##  # ',
  ' ##  ##  ## ',
  '  ##  ##  # ',
  '   ##  ##   ',
  '    ##      ',
]

const MARK_SIZE = 120
/** Full morph cycle: solid hold → crystal dissolve → ascii hold → reform → solid */
const CYCLE_MS = 4600
const FACETS = 9

export type AsciiGrapeLoaderVariant = 'morph' | 'lucide' | 'ascii' | 'compare'

export function AsciiGrapeLoader({
  showWordmark = false,
  variant = 'morph',
}: {
  showWordmark?: boolean
  /**
   * `morph` (default): crystalline dissolve between solid Lucide and ASCII.
   * `compare`: side-by-side for picking.
   */
  variant?: AsciiGrapeLoaderVariant
}) {
  const [tick, setTick] = useState(0)
  const [densityPhase, setDensityPhase] = useState(0)
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mq.matches)
    const onChange = () => setReduceMotion(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (reduceMotion || variant !== 'morph') return
    let raf = 0
    const loop = () => {
      setTick(Date.now())
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => cancelAnimationFrame(raf)
  }, [reduceMotion, variant])

  useEffect(() => {
    if (reduceMotion) return
    const id = window.setInterval(() => {
      setDensityPhase(p => (p + 1) % DENSITY.length)
    }, 120)
    return () => window.clearInterval(id)
  }, [reduceMotion])

  const morph = morphState(tick, reduceMotion || variant !== 'morph')
  const pulse =
    reduceMotion || variant === 'morph'
      ? 1
      : 0.72 + (densityPhase / (DENSITY.length - 1)) * 0.28

  if (variant === 'compare') {
    return (
      <LoaderShell showWordmark={showWordmark} wordmarkOpacity={pulse}>
        <View
          style={[
            a.flex_row,
            a.align_end,
            a.justify_center,
            a.gap_2xl,
            {opacity: pulse},
          ]}>
          <GrapeIcon width={MARK_SIZE} fill={PURPLE} />
          <AsciiMark
            densityPhase={densityPhase}
            reveal={1}
            crystalline={false}
          />
        </View>
      </LoaderShell>
    )
  }

  if (variant === 'lucide') {
    return (
      <LoaderShell showWordmark={showWordmark} wordmarkOpacity={pulse}>
        <View style={{opacity: pulse}}>
          <GrapeIcon width={MARK_SIZE} fill={PURPLE} />
        </View>
      </LoaderShell>
    )
  }

  if (variant === 'ascii') {
    return (
      <LoaderShell showWordmark={showWordmark} wordmarkOpacity={pulse}>
        <View style={{opacity: pulse}}>
          <AsciiMark
            densityPhase={densityPhase}
            reveal={1}
            crystalline={false}
          />
        </View>
      </LoaderShell>
    )
  }

  return (
    <LoaderShell showWordmark={showWordmark} wordmarkOpacity={1}>
      <View
        style={[
          {
            width: MARK_SIZE,
            height: MARK_SIZE,
            position: 'relative',
            transform: [
              {scale: morph.scale},
              {rotate: `${morph.rotateDeg}deg`},
            ],
          },
        ]}>
        <View
          style={[
            a.absolute,
            a.inset_0,
            a.align_center,
            a.justify_center,
            {opacity: morph.solid},
          ]}>
          <GrapeIcon width={MARK_SIZE} fill={PURPLE} />
        </View>
        <View
          style={[
            a.absolute,
            a.inset_0,
            a.align_center,
            a.justify_center,
            {opacity: morph.glint},
          ]}
          pointerEvents="none">
          <GrapeIcon width={MARK_SIZE} fill={GLINT} />
        </View>
        <View
          style={[
            a.absolute,
            a.inset_0,
            a.align_center,
            a.justify_center,
            {opacity: morph.ascii},
          ]}>
          <AsciiMark
            densityPhase={densityPhase}
            reveal={morph.facetReveal}
            crystalline={morph.inTransition}
            dissolveOut={morph.dissolveOut}
          />
        </View>
      </View>
    </LoaderShell>
  )
}

function AsciiMark({
  densityPhase,
  reveal,
  crystalline,
  dissolveOut = false,
}: {
  densityPhase: number
  reveal: number
  crystalline: boolean
  dissolveOut?: boolean
}) {
  const charset = crystalline ? CRYSTAL : DENSITY

  return (
    <View style={[a.align_center, a.justify_center]}>
      {ASCII_GRAPE_MASK.map((row, rowIndex) => (
        <Text
          key={rowIndex}
          style={flatten([
            {
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 14,
              lineHeight: 16,
              letterSpacing: 1.2,
              color: crystalline ? GLINT : PURPLE,
            },
          ])}>
          {row
            .split('')
            .map((c, colIndex) => {
              if (c === ' ') return ' '
              const thresh = facetThreshold(rowIndex, colIndex)
              const visible = dissolveOut ? reveal < thresh : reveal >= thresh
              if (!visible) return ' '
              const i =
                (rowIndex * 3 + colIndex + densityPhase) % charset.length
              return charset[i]
            })
            .join('')}
        </Text>
      ))}
    </View>
  )
}

function LoaderShell({
  showWordmark,
  wordmarkOpacity,
  children,
}: {
  showWordmark: boolean
  wordmarkOpacity: number
  children: ReactNode
}) {
  return (
    <View
      style={[
        a.align_center,
        a.justify_center,
        a.gap_lg,
        {top: -50, width: '100%'},
      ]}
      accessibilityRole="image"
      accessibilityLabel="GrapeVyne"
      accessibilityHint="">
      {children}
      {showWordmark ? (
        <View
          style={[
            a.align_center,
            a.justify_center,
            {width: 180, opacity: wordmarkOpacity},
          ]}>
          <Logotype width={180} fill={PURPLE} />
        </View>
      ) : null}
    </View>
  )
}

type MorphState = {
  solid: number
  ascii: number
  glint: number
  scale: number
  rotateDeg: number
  facetReveal: number
  inTransition: boolean
  dissolveOut: boolean
}

/**
 * Timeline (fraction of CYCLE_MS):
 * 0.00–0.26 solid hold
 * 0.26–0.44 crystal dissolve → ascii (faceted)
 * 0.44–0.68 ascii hold
 * 0.68–0.86 crystal reform → solid
 * 0.86–1.00 solid hold
 */
function morphState(now: number, freezeSolid: boolean): MorphState {
  if (freezeSolid) {
    return {
      solid: 1,
      ascii: 0,
      glint: 0,
      scale: 1,
      rotateDeg: 0,
      facetReveal: 0,
      inTransition: false,
      dissolveOut: false,
    }
  }

  const t = (now % CYCLE_MS) / CYCLE_MS

  if (t < 0.26) {
    return baseMorph({solid: 1, ascii: 0})
  }

  if (t < 0.44) {
    const raw = (t - 0.26) / 0.18
    const p = facet(easeInOut(raw))
    const glint = crystalGlint(raw)
    return {
      solid: 1 - p * 0.95,
      ascii: Math.min(1, p * 1.15),
      glint,
      scale: 1 + glint * 0.08,
      rotateDeg: (raw - 0.5) * 4,
      facetReveal: p,
      inTransition: true,
      dissolveOut: false,
    }
  }

  if (t < 0.68) {
    return baseMorph({solid: 0, ascii: 1})
  }

  if (t < 0.86) {
    const raw = (t - 0.68) / 0.18
    const p = facet(easeInOut(raw))
    const glint = crystalGlint(raw)
    return {
      solid: Math.min(1, p * 1.15),
      ascii: 1 - p * 0.95,
      glint,
      scale: 1 + glint * 0.08,
      rotateDeg: (0.5 - raw) * 4,
      facetReveal: 1 - p,
      inTransition: true,
      dissolveOut: true,
    }
  }

  return baseMorph({solid: 1, ascii: 0})
}

function baseMorph({solid, ascii}: {solid: number; ascii: number}): MorphState {
  return {
    solid,
    ascii,
    glint: 0,
    scale: 1,
    rotateDeg: 0,
    facetReveal: ascii,
    inTransition: false,
    dissolveOut: false,
  }
}

/** Snap progress onto crystal facets for a refractive feel. */
function facet(x: number) {
  const clamped = Math.min(1, Math.max(0, x))
  return Math.round(clamped * FACETS) / FACETS
}

/** Bright mid-dissolve glint, quieter at edges. */
function crystalGlint(raw: number) {
  const bell = Math.sin(Math.min(1, Math.max(0, raw)) * Math.PI)
  return facet(bell * bell)
}

/** Stable per-cell threshold so facets catch light in a diagonal crystal sweep. */
function facetThreshold(row: number, col: number) {
  return ((row * 5 + col * 7 + (row ^ col) * 3) % (FACETS + 1)) / FACETS
}

function easeInOut(x: number) {
  const clamped = Math.min(1, Math.max(0, x))
  return clamped * clamped * (3 - 2 * clamped)
}
